import { useListItem, useMergeRefs } from '@floating-ui/react'
import { IconProps } from '@tabler/icons-react'
import { ComponentProps, ComponentType, KeyboardEvent, MouseEvent, MouseEventHandler, Ref } from 'react'
import { Link } from 'react-router'

import { TextColorClass } from '../../types/types'
import { KeyboardShortcut } from '../../utils/keyboardShortcuts'
import { AstraBadge } from '../AstraBadge/AstraBadge'
import { AllowedIcon, IconComponent } from '../AstraIcon/AstraIcon'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import { DropdownItemContent } from './DropdownItemContent'
import { useDropdownMenuContext } from './DropdownMenuContext'
import { DROPDOWN_ITEM_ACTIVE_CLASS, DROPDOWN_ITEM_CLASS } from './dropdownStyles'

type MenuItem = {
    value?: string
    type?: 'action' | 'link' | 'checkbox'
    /**
     * The text to display for the dropdown item. This is required.
     */
    title: string

    /**
     * An optional React functional component for displaying an icon to the left of the title.
     */
    icon?: ComponentType<IconProps> | IconComponent
    /**
     * An optional React functional component for displaying an icon to the right of the title.
     */
    iconRight?: ComponentType<IconProps> | IconComponent

    /**
     * Secondary text at the end of the row, for a count or a value the row is about, such as
     * `16 pasienter`. It is muted and never the item's label, so the title must still read on its own.
     */
    trailingText?: string
    /**
     * A badge at the end of the row, taking the AstraBadge props. `size` is fixed to `small`, since a
     * larger badge outgrows the row, and `fullWidth` would push the rest of the row out. Its `icon` is
     * the component itself, the way `icon` and `iconRight` take one; AstraBadge sizes it to match the badge.
     */
    badge?: Omit<ComponentProps<typeof AstraBadge>, 'size' | 'fullWidth' | 'icon'> & {
        icon?: AllowedIcon
    }

    href?: string
    /**
     * Determines if the dropdown item is hidden.
     * If `true`, the item will not be visible.
     */
    isHidden?: boolean

    /**
     * Indicates if the dropdown item is currently active or selected.
     */
    isActive?: boolean
    /**
     * An optional color to apply to the text of the dropdown item.
     */
    color?: TextColorClass
    /**
     * An optional ref to the row's element: a div, or an anchor for an enabled link.
     */
    ref?: Ref<HTMLElement>
    /**
     * is the option disabled
     */
    disabled?: boolean
    /**
     * is the info option disabled
     */
    disabledInfo?: string

    /**
     * Additional optional classNames.
     */
    className?: string
}

export type MenuItemAction = {
    type: 'action'
    /**
     * A function that gets executed when the dropdown item is selected.
     */
    action?: (event: Event) => void
    shortcut?: KeyboardShortcut
} & MenuItem

export type MenuItemCheckbox = {
    type: 'checkbox'
    /**
     * Whether the item is checked. Always controlled: a menu closes and remounts, so it is the wrong
     * place to keep the answer. `indeterminate` renders the mixed mark and toggles to checked.
     */
    checked: boolean | 'indeterminate'
    /**
     * Called with the value the item should take. Toggling does not close the menu, since a run of
     * checkboxes is normally worked through in one visit.
     */
    onCheckedChange: (checked: boolean) => void
    shortcut?: KeyboardShortcut
} & MenuItem

export type MenuItemLink = {
    type: 'link'
    /**
     * The URL to navigate to when the dropdown item is clicked.
     */
    url: string
} & MenuItem

export type AstraDropdownItemProps = {
    option: MenuItemAction | MenuItemLink | MenuItemCheckbox
    onMouseEnter?: MouseEventHandler<HTMLElement>
    onMouseLeave?: MouseEventHandler<HTMLElement>
    'data-test'?: string
}

export const AstraDropdownItem = ({ option, 'data-test': dataTest, ...props }: AstraDropdownItemProps) => {
    const menuContext = useDropdownMenuContext()
    const { ref: listItemRef, index } = useListItem({ label: option.title })
    const setRowRef = useMergeRefs<HTMLElement>([listItemRef, option.ref])
    // In a panel that searches, the caret stays in the field and a row is marked instead of focused.
    const isSearchActive = menuContext?.activeIndex === index

    if (option.isHidden) return null

    const disabled = option.disabled ?? false

    const activate = (event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return

        if (option.type === 'action') {
            option.action?.(event.nativeEvent)
            // An action that calls preventDefault keeps the menu open, so it has to be read before the
            // preventDefault below.
            const shouldClose = !event.nativeEvent.defaultPrevented
            event.preventDefault()
            if (shouldClose) menuContext?.closeMenu()
            return
        }

        event.preventDefault()
        // A click anywhere on the row toggles, including on the checkbox itself: the box is presentational
        // and its click bubbles to here, so the row is the only thing that ever changes the value.
        if (option.type === 'checkbox') {
            option.onCheckedChange(option.checked !== true)
            menuContext?.focusSearch?.()
        }
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') activate(event)
    }

    const registeredItemProps = {
        ...props,
        onMouseEnter: (event: MouseEvent<HTMLElement>) => {
            // Moving onto an ordinary row is how the user says they are done with the panel hanging off
            // the menu this row belongs to.
            menuContext?.setOpenSubmenu(null)
            props.onMouseEnter?.(event)
        },
        ref: setRowRef ?? undefined,
        id: menuContext?.itemIdPrefix ? `${menuContext.itemIdPrefix}-${index}` : undefined,
        'data-active': isSearchActive || undefined,
        'data-test': dataTest,
        'aria-disabled': disabled,
        className: astraTwMerge(
            DROPDOWN_ITEM_CLASS,
            isSearchActive && DROPDOWN_ITEM_ACTIVE_CLASS,
            option.color,
            option.isActive && 'bg-neutral-subtle-hover',
            disabled && 'bg-surface-disabled opacity-50 hover:cursor-not-allowed',
            disabled && option.type === 'link' && 'pointer-events-none',
            option.className
        ),
    }

    const itemProps = menuContext?.getItemProps(registeredItemProps) ?? registeredItemProps
    const tabIndex = disabled ? -1 : 0
    const content = <DropdownItemContent option={option} disabled={disabled} />

    if (option.type === 'link' && !disabled) {
        return (
            <Link
                {...itemProps}
                to={option.url}
                role="menuitem"
                tabIndex={tabIndex}
                onClick={() => menuContext?.closeMenu()}
                onKeyDown={event => {
                    if (event.key === ' ') {
                        event.preventDefault()
                        event.currentTarget.click()
                    }
                }}
            >
                {content}
            </Link>
        )
    }

    if (option.type === 'checkbox') {
        return (
            <div
                {...itemProps}
                role="menuitemcheckbox"
                aria-checked={option.checked === 'indeterminate' ? 'mixed' : option.checked}
                tabIndex={tabIndex}
                onClick={activate}
                onKeyDown={handleKeyDown}
            >
                {content}
            </div>
        )
    }

    return (
        <div {...itemProps} role="menuitem" tabIndex={tabIndex} onClick={activate} onKeyDown={handleKeyDown}>
            {content}
        </div>
    )
}
